/**
 * Small pieces the Funding & Growth panels share: the card surface, a load
 * hook that keeps the last good data on screen while it refetches, and the
 * plain-language labels for statuses and submission channels.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

export const CARD = 'rounded-[10px] border border-v2border bg-surface shadow-v2-1';
export const MUTED = 'text-[13px] text-ink-500';

/** Load once, reload on demand; a reload never blanks what is already shown. */
export function useLoad<T>(load: () => Promise<T>, deps: React.DependencyList) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const loadRef = useRef(load);
  loadRef.current = load;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setData(await loadRef.current());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, error, loading, reload, setData };
}

export const STATUS_LABEL: Record<string, string> = {
  new: 'New',
  reviewing: 'Reviewing',
  pursuing: 'Pursuing',
  outreach_drafted: 'Outreach drafted',
  outreach_sent: 'Outreach sent',
  responded: 'Responded',
  won: 'Won',
  dismissed: 'Dismissed',
  draft: 'Draft',
  approved: 'Approved',
  sent: 'Sent',
  failed: 'Failed',
  proposed: 'Proposed',
  confirmed: 'Confirmed',
  rejected: 'Rejected',
};

/** The submission router's channel keys, as a person would say them. */
export const CHANNEL_LABEL: Record<string, string> = {
  clearinghouse_837: '837 claim through a clearinghouse',
  portal: 'Plan portal upload',
  workbook: 'Plan invoice workbook',
  sftp: 'SFTP file drop',
  secure_email: 'Secure email',
  paper: 'Paper',
};

export const channelLabel = (c?: string | null) => (c ? CHANNEL_LABEL[c] ?? c.replace(/_/g, ' ') : 'Channel set when the funder is added');

const PILL: Record<string, string> = {
  success: 'bg-success-soft text-success-ink',
  attention: 'bg-attention-soft text-attention-ink',
  accent: 'bg-v2accent-soft text-v2accent-ink',
  neutral: 'bg-paper-2 text-ink-500',
  danger: 'bg-danger-soft text-ink-700',
};

export const Pill: React.FC<{ tone?: keyof typeof PILL; children: React.ReactNode }> = ({ tone = 'neutral', children }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${PILL[tone]}`}>{children}</span>
);

export const ErrorNote: React.FC<{ message: string }> = ({ message }) => (
  <p role="alert" className="rounded-[10px] border border-v2border bg-attention-soft px-3 py-2 text-[13px] text-attention-ink">
    {message}
  </p>
);

/** "CA, NV" <-> ['CA', 'NV'] for the comma-separated profile fields. */
export const toList = (s: string) =>
  s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
export const fromList = (l?: string[] | null) => (l ?? []).join(', ');

export const formatDate = (d?: string | null) => {
  if (!d) return null;
  const date = new Date(d.length === 10 ? `${d}T00:00:00` : d);
  return Number.isNaN(date.getTime()) ? d : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};
